import "./PayoffChartComponent.scss";
import {
    IFilterService,
    IOnChanges,
    IOnChangesObject,
    IOnDestroy,
    IOnInit,
    IRootElementService,
    ITimeoutService
} from "angular";
import {generateDefaultChartOptions} from "./defaults/DefaultChartOptions";
import {Optional} from "../../../../js/util/Optional";
import {LegendClickCallback, PayoffChartData} from "./types/PayoffChartData";
import {formatDate} from "../../../../js/util/DateTimeUtil";
import {ObjectUtil} from "../../../../js/util/ObjectUtil";
import CONFIG from "./defaults/ChartConfig";
import {Subscription} from "rxjs/Subscription";
import {SystemEventService} from "../../../../services/system-state/SystemEventService";
import {SystemEvent} from "../../../../services/system-state/SystemEvent";

export const PayoffChart = "tfPayoffChart";

export type OptionsFunction = ($filter: IFilterService, priceSteps: Array<any>, points: boolean) => any;

export function tfPayoffChart() {
    return {
        templateUrl: "./app/components/charts/payoff/payoff-chart/PayoffChartComponent.html",
        bindings: {
            height: "<",
            optionsFn: "<",
            data: "<",
            onLegendClick: "&"
        },
        controller: PayoffChartComponentController,
        controllerAs: "ctrl"
    };
}

export class PayoffChartComponentController implements IOnInit, IOnChanges, IOnDestroy {

    public height: number;
    public optionsFn?: OptionsFunction;
    public data: PayoffChartData;
    public onLegendClick: LegendClickCallback;
    public chartConfig = CONFIG;

    public chartData: Array<any>;
    public chartOptions: any;
    private api: any;
    private stateSub = Subscription.EMPTY;

    /** @ngInject */
    public constructor(private $filter: IFilterService,
                       private $timeout: ITimeoutService,
                       private systemEventService: SystemEventService) {
    }

    public $onInit(): void {
        this.stateSub = this.systemEventService.subscribe(this.onSystemEvent.bind(this));
    }

    private onSystemEvent(event: SystemEvent) {
        if (event === SystemEvent.WINDOW_BLUR) {
            this.hideTooltip();
        } else if (event === SystemEvent.VIEW_RESIZE) {
            this.refresh();
        }
    }

    private hideTooltip() {
        this.findCurrentTooltip()
            .ifPresent(tt => tt.hidden(true));
    }

    public $onChanges(changes: IOnChangesObject): void {
        this.refreshChart(this.data, this.optionsFn);
    }

    private refreshChart(data: PayoffChartData, optionsFn?: OptionsFunction) {
        if (data != null) {
            this.ensureTooltipRemoved();
            this.chartData = this.generateChartData(data);
            this.chartOptions = this.generateChartOptions(data, optionsFn);
        }
    }

    // angular-nv doesn't clean up after itself properly, so we have to make sure the old tooltip is gone
    private ensureTooltipRemoved() {
        this.findCurrentTooltip()
            .map(tooltip => tooltip.id())
            .map(id => document.querySelector(`#${id}`))
            .ifPresent(el => el.remove());
    }

    private findCurrentTooltip() {
        return Optional.of(this.api)
                       .map(api => api.getScope())
                       .map(scope => scope.chart)
                       .map(chart => chart.interactiveLayer)
                       .map(layer => layer.tooltip);
    }

    private generateChartOptions(data: PayoffChartData, optionsFn?: OptionsFunction) {
        const summary = data.payoffSummary;
        const defaults = this.defaultChartOptions(this.$filter, summary.priceSteps, summary.points);
        const overrides = Optional.of(optionsFn)
                                  .map(fn => fn(this.$filter, summary.priceSteps, summary.points))
                                  .orElse({});
        return Object.assign({}, defaults, overrides);
    }

    private defaultChartOptions($filter: IFilterService, priceSteps: Array<any>, points: boolean) {
        const height = Optional.of(this.height).orElse(300);
        const defaults = generateDefaultChartOptions(height, $filter, priceSteps, points);
        const clickHandlers = this.defaultClickHandlers();
        return ObjectUtil.merge({}, defaults, clickHandlers);
    }

    private defaultClickHandlers() {
        return {
            chart: {
                legend: {
                    dispatch: {
                        legendClick: (series: NVD3ChartSeries) => this.legendClick(series)
                    }
                }
            }
        };
    }

    private legendClick(series: NVD3ChartSeries) {
        // Timeout is required as the click is called before the legend state has been updated
        this.$timeout(() => {
            const container = this.api.getScope().chart.container;
            const data: Array<NVD3ChartSeries> = d3.select(container).datum();
            this.onLegendClick({clicked: series, data});
        });
    }

    private generateChartData(data: PayoffChartData): NVD3ChartData {
        const summary = data.payoffSummary;
        const seriesData: NVD3ChartData = summary.dateSteps.map((d: any) => {

            const key = formatDate(d, "MMM Do YYYY");
            const state = Optional.of(data.seriesState)
                                  .map(ss => ss[key])
                                  .orElse(false);

            return {
                key: formatDate(d, "MMM Do YYYY"),
                values: [],
                disabled: state
            };
        });

        const steps = (summary.priceSteps.length - 1) / 2;
        for (let i = 0; i < summary.priceSteps.length; i++) {

            for (let j = 0; j < summary.dateSteps.length; j++) {

                const dataPosition = j * summary.priceSteps.length + i;

                seriesData[j].values.push({
                    x: i - steps,
                    y: summary.payOffData[dataPosition]
                });
            }
        }

        return seriesData;
    }

    public chartReady($scope: any, $elem: IRootElementService) {
        this.api = $scope.api;
        this.refreshChart(this.data, this.optionsFn);
    }

    private refresh() {
        Optional.of(this.api).ifPresent(api => api.update());
    }

    public $onDestroy() {
        this.ensureTooltipRemoved();
        this.stateSub.unsubscribe();
    }
}
